import type { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import type { DrugScreenPayload } from '@/forms/DrugScreenForm/types';
import type { DrugScreensParams } from '@/repositories/DrugScreenRepository/types';
import {
  DefaultDrugScreenData,
  DrugScreen,
  DrugScreenPayout,
  PersonDrugScreensCounts
} from '@/types/drugScreen';
import type PaginatedResponse from '@/types/paginatedResponse';
import type RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export const drugScreenRepository = {
  async getDefaultDrugScreenData({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<RepositoryResponse<DefaultDrugScreenData>> {
    try {
      const path = `/drug_screens/${personId}/default_drug_screen_data/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get default drug screen data.');
    }
  },

  async getDrugScreen({
    host,
    drugScreenId
  }: {
    host: string;
    drugScreenId?: string;
  }): Promise<RepositoryResponse<DrugScreen> | undefined> {
    if (drugScreenId) {
      try {
        const path = `/api/dugout/drug_screens/${drugScreenId}/`;
        const response = await ajax.get({
          url: `${host}${path}`
        });

        return {
          data: response.data,
          isError: false
        };
      } catch (error) {
        logAjaxError(error);
        return defaultRepositoryError(error as AxiosError, 'Failed to get drug screen data.');
      }
    }
  },

  async getDrugScreens({
    host,
    params
  }: {
    host: string;
    params: DrugScreensParams;
  }): Promise<RepositoryResponse<PaginatedResponse<DrugScreen[]>>> {
    try {
      const path = `/api/dugout/drug_screens/`;
      const response = await ajax.get({
        url: `${host}${path}`,
        params: {
          ...params
        }
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get drug screens.');
    }
  },

  async getPersonDrugScreensCounts({
    host,
    personId
  }: {
    host: string;
    personId: string;
  }): Promise<RepositoryResponse<PersonDrugScreensCounts>> {
    try {
      const path = `/api/dugout/drug_screens/counts/${personId}/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(
        error as AxiosError,
        'Failed to get person drug screens stats.'
      );
    }
  },

  async createDrugScreen({
    host,
    values
  }: {
    host: string;
    values: DrugScreenPayload;
  }): Promise<RepositoryResponse<DrugScreen>> {
    try {
      const path = '/api/dugout/drug_screens/';
      const response = await ajax.post<DrugScreenPayload, AxiosResponse<DrugScreen>>({
        url: `${host}${path}`,
        data: values
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to save drug screen.');
    }
  },

  async updateDrugScreen({
    host,
    drugScreenId,
    values
  }: {
    host: string;
    drugScreenId: string;
    values: DrugScreenPayload;
  }): Promise<RepositoryResponse<DrugScreen>> {
    try {
      const path = `/api/dugout/drug_screens/${drugScreenId}/`;
      const response = await ajax.patch<DrugScreenPayload, AxiosResponse<DrugScreen>>({
        url: `${host}${path}`,
        data: values
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to update drug screen.');
    }
  },

  async previewDrugScreen({
    host,
    drugScreenId,
    values
  }: {
    host: string;
    drugScreenId?: string;
    values: DrugScreenPayload;
  }): Promise<RepositoryResponse<DrugScreenPayout>> {
    try {
      if (drugScreenId) {
        const path = `/api/dugout/drug_screens/${drugScreenId}/preview_payout/`;
        const response = await ajax.patch<DrugScreenPayload, AxiosResponse<DrugScreenPayout>>({
          url: `${host}${path}`,
          data: values
        });

        return {
          data: response.data,
          isError: false
        };
      } else {
        const path = '/api/dugout/drug_screens/preview_payout/';
        const response = await ajax.post<DrugScreenPayload, AxiosResponse<DrugScreenPayout>>({
          url: `${host}${path}`,
          data: values
        });

        return {
          data: response.data,
          isError: false
        };
      }
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to save drug screen.');
    }
  }
};

const DrugScreenRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getDefaultDrugScreenData(
      personId: string
    ): Promise<RepositoryResponse<DefaultDrugScreenData>> {
      return drugScreenRepository.getDefaultDrugScreenData({
        host,
        personId
      });
    },

    async getDrugScreen(
      drugScreenId?: string
    ): Promise<RepositoryResponse<DrugScreen> | undefined> {
      return drugScreenRepository.getDrugScreen({
        host,
        drugScreenId
      });
    },

    async getDrugScreens(
      params: DrugScreensParams
    ): Promise<RepositoryResponse<PaginatedResponse<DrugScreen[]>>> {
      return drugScreenRepository.getDrugScreens({
        host,
        params
      });
    },

    async getPersonDrugScreensCounts(
      personId: string
    ): Promise<RepositoryResponse<PersonDrugScreensCounts>> {
      return drugScreenRepository.getPersonDrugScreensCounts({
        host,
        personId
      });
    },

    async createDrugScreen(values: DrugScreenPayload): Promise<RepositoryResponse<DrugScreen>> {
      return drugScreenRepository.createDrugScreen({
        host,
        values
      });
    },

    async updateDrugScreen(
      drugScreenId: string,
      values: DrugScreenPayload
    ): Promise<RepositoryResponse<DrugScreen>> {
      return drugScreenRepository.updateDrugScreen({
        host,
        drugScreenId,
        values
      });
    },

    async previewDrugScreen({
      drugScreenId,
      values
    }: {
      drugScreenId?: string;
      values: DrugScreenPayload;
    }): Promise<RepositoryResponse<DrugScreenPayout>> {
      return drugScreenRepository.previewDrugScreen({
        host,
        drugScreenId,
        values
      });
    }
  };
};

export default DrugScreenRepository;
